import React, { useEffect, useMemo, useState } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

import PhotoLightbox from './PhotoLightbox';

const WIDE_RATIO = 2.0;

const segmentPhotos = (photos, ratios) => {
    const segments = [];
    let normalBatch = [];

    const flushNormals = () => {
        if (normalBatch.length === 0) return;
        segments.push({ type: 'masonry', photos: normalBatch });
        normalBatch = [];
    };

    photos.forEach((photo) => {
        const ratio = ratios[photo.filename];
        const isWide = typeof ratio === 'number' && ratio >= WIDE_RATIO;

        if (isWide) {
            flushNormals();
            segments.push({ type: 'panorama', photo });
        } else {
            normalBatch.push(photo);
        }
    });

    flushNormals();
    return segments;
};

const PhotoGallery = ({ photos, category }) => {
    const [ratios, setRatios] = useState({});
    const [lightboxPhoto, setLightboxPhoto] = useState(null);

    useEffect(() => {
        let cancelled = false;

        photos.forEach((photo) => {
            const img = new Image();
            img.onload = () => {
                if (cancelled || !img.naturalWidth || !img.naturalHeight) return;
                setRatios((prev) => {
                    if (prev[photo.filename] !== undefined) return prev;
                    return {
                        ...prev,
                        [photo.filename]: img.naturalWidth / img.naturalHeight,
                    };
                });
            };
            img.src = photo.src;
        });

        return () => {
            cancelled = true;
        };
    }, [photos]);

    const segments = useMemo(
        () => segmentPhotos(photos, ratios),
        [photos, ratios]
    );

    const openLightbox = (photo) => setLightboxPhoto(photo);
    const closeLightbox = () => setLightboxPhoto(null);

    const renderThumb = (photo, className) => (
        <button
            type="button"
            key={photo.filename}
            className={className}
            onClick={() => openLightbox(photo)}
            aria-label={`View ${photo.filename}`}
        >
            <LazyLoadImage
                alt={photo.filename}
                src={photo.src}
                placeholderSrc={photo.src}
                effect="blur"
                style={{ width: '100%', display: 'block' }}
            />
        </button>
    );

    return (
        <div className="photo-gallery">
            {segments.map((segment, index) => {
                if (segment.type === 'panorama') {
                    return (
                        <div className="photo-panorama" key={`pano-${segment.photo.filename}`}>
                            {renderThumb(segment.photo, 'photo-thumb photo-thumb--panorama')}
                        </div>
                    );
                }

                return (
                    <ResponsiveMasonry
                        key={`masonry-${index}`}
                        columnsCountBreakPoints={{ 800: 1, 1000: 2 }}
                        className="photo-masonry"
                    >
                        <Masonry gutter="5vw">
                            {segment.photos.map((photo) =>
                                renderThumb(photo, 'photo-thumb')
                            )}
                        </Masonry>
                    </ResponsiveMasonry>
                );
            })}

            <PhotoLightbox
                open={Boolean(lightboxPhoto)}
                photo={
                    lightboxPhoto
                        ? { ...lightboxPhoto, category }
                        : null
                }
                onClose={closeLightbox}
            />
        </div>
    );
};

export default PhotoGallery;
